import React, { useState, useEffect, useRef } from "react";
import {
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiAlertCircle,
  FiCheckCircle,
  FiRefreshCw,
  FiShield,
  FiBookOpen,
  FiUser,
  FiCamera,
  FiCheck,
  FiX,
} from "react-icons/fi";
import api from "../../../connector/api";

// Right Sidebar Import
import StudentRightSidebar from "../sidebar/StudentRightSidebar";

// Student Tab
import StudentAcademicTab from "./student/StudentAcademicTab";
import StudentAttendanceTab from "./student/StudentAttendanceTab";
import StudentBehaviorTab from "./student/StudentBehaviorTab";
import StudentDressCodeTab from "./student/StudentDressCodeTab";

export default function StudentDetailTab({
  student: initialStudent,
  onBack,
  onEdit, // Can still trigger external parent modal if needed
  onDelete,
}) {
  const [student, setStudent] = useState(initialStudent);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("info"); // 'info' | 'passCard' | 'departureLogs' | 'behavior' | 'infractions'
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // --- Profile Edit State ---
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: "",
    last_name: "",
    gender: "M",
    student_id_number: "",
    phone_number: "",
    email: "",
  });

  const fileInputRef = useRef(null);

  // Sync initial student prop with local state & form data
  useEffect(() => {
    if (initialStudent) {
      setStudent(initialStudent);
      populateForm(initialStudent);
    }
  }, [initialStudent]);

  const populateForm = (data) => {
    setEditFormData({
      first_name: data.first_name || "",
      last_name: data.last_name || "",
      gender: data.gender || "M",
      student_id_number: data.student_id_number || "",
      phone_number: data.phone_number || "",
      email: data.email || "",
    });
  };

  // Fetch full student details
  useEffect(() => {
    if (!initialStudent?.id) return;

    const fetchFullDetail = async () => {
      setDetailLoading(true);
      try {
        const res = await api.get(`/students/${initialStudent.id}/`);
        setStudent(res.data);
        populateForm(res.data);
      } catch (err) {
        console.error("Failed to fetch full student details:", err);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchFullDetail();
  }, [initialStudent?.id]);

  // Handle Form Inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit Profile Updates to Backend
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.patch(
        `/students/${student.id}/`,
        editFormData
      );
      setStudent(response.data);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to update student profile:", err);
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle Avatar Image Upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("profile_picture", file);

    setSaving(true);
    try {
      const res = await api.patch(`/students/${student.id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStudent(res.data);
    } catch (err) {
      console.error("Error uploading profile image:", err);
      alert("Failed to update profile image.");
    } finally {
      setSaving(false);
    }
  };

  if (!student) return null;

  const fullName = `${
    student.last_name || ""
  }, ${student.first_name || ""}`.trim();
  const infractions = student.infractions || [];

  return (
    <div className="space-y-4 animate-in fade-in duration-150">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between border-b border-zinc-200/80 pb-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>Back to Student List</span>
        </button>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              <span>Edit Profile</span>
            </button>
          ) : (
            <button
              onClick={() => {
                setIsEditing(false);
                populateForm(student);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200/70 text-zinc-700 text-xs font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FiX className="w-3.5 h-3.5" />
              <span>Cancel Editing</span>
            </button>
          )}

          <button
            onClick={() => onDelete(student)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg transition-colors cursor-pointer"
          >
            <FiTrash2 className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Left Column: Profile Banner & Active Content View */}
        <div
          className={`space-y-4 transition-all duration-300 ${
            isSidebarOpen ? "lg:col-span-8 xl:col-span-9" : "lg:col-span-12"
          }`}
        >
          {/* Header Profile Card */}
          <div className="bg-white rounded-lg border border-zinc-200/80 p-6 shadow-2xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div className="flex items-center gap-5">
                {/* Profile Photo with Upload Trigger (Enlarged to 96px x 96px / w-24 h-24) */}
                <div className="relative group shrink-0">
                  {student.profile_picture ? (
                    <img
                      src={student.profile_picture}
                      alt={fullName}
                      className="w-24 h-24 rounded-full object-cover border-2 border-zinc-200/80 shadow-xs"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-zinc-100 border-2 border-zinc-200/80 flex items-center justify-center font-bold text-zinc-700 text-2xl uppercase shadow-xs">
                      {student.last_name?.[0] || "S"}
                      {student.first_name?.[0] || ""}
                    </div>
                  )}

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                    accept="image/*"
                    className="hidden"
                  />

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    title="Change Photo"
                    className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <FiCamera className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-zinc-900">
                    {fullName}
                  </h2>
                  <p className="text-xs font-medium text-zinc-500">
                    ID: {student.student_id_number || "Not Assigned"} •
                    Classroom: {student.classroom_name || "Unassigned"}
                  </p>
                  <div className="pt-1 flex items-center gap-2">
                    {student.has_uniform_infraction ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200/60">
                        <FiAlertCircle className="w-3 h-3" /> Infraction
                        Recorded Today
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                        <FiCheckCircle className="w-3 h-3" /> Uniform Compliant
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {detailLoading && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> Fetching
                  details...
                </div>
              )}
            </div>
          </div>

          {/* Active Tab Panel Content */}
          <div className="rounded-lg">

            {activeTab === "academic" && <StudentAcademicTab student={student} />}
            {activeTab === "attendance" && <StudentAttendanceTab student={student} />}
            {activeTab === "behavior" && <StudentBehaviorTab student={student} />}
            {activeTab === "dresscode" && <StudentDressCodeTab student={student} />}
            
            {/* View 1: Overview & Profile (Includes Edit Mode) */}
            {activeTab === "info" && (
              <div className="space-y-6 text-xs">
                {isEditing ? (
                  /* Editable Profile Form */
                  <form onSubmit={handleSaveProfile} className="space-y-5">
                    <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                      <h3 className="font-bold text-zinc-900 text-xs flex items-center gap-1.5">
                        <FiEdit2 className="w-3.5 h-3.5 text-blue-600" />
                        Edit Student Profile
                      </h3>
                      <span className="text-[11px] text-zinc-400">
                        Update personal details
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          value={editFormData.first_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          value={editFormData.last_name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          Student ID Number
                        </label>
                        <input
                          type="text"
                          name="student_id_number"
                          value={editFormData.student_id_number}
                          onChange={handleInputChange}
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          Gender
                        </label>
                        <select
                          name="gender"
                          value={editFormData.gender}
                          onChange={handleInputChange}
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        >
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          Contact Phone
                        </label>
                        <input
                          type="text"
                          name="phone_number"
                          value={editFormData.phone_number}
                          onChange={handleInputChange}
                          placeholder="e.g. +1 234 567 890"
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        />
                      </div>

                      <div>
                        <label className="block text-zinc-600 font-semibold mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={editFormData.email}
                          onChange={handleInputChange}
                          placeholder="e.g. student@school.edu"
                          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-3.5 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-1.5 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg text-xs font-medium transition-colors shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                      >
                        {saving ? (
                          <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <FiCheck className="w-3.5 h-3.5" />
                        )}
                        <span>{saving ? "Saving..." : "Save Changes"}</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Read-only Profile Display */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h3 className="font-bold text-zinc-400 uppercase tracking-wider text-[11px] border-b border-zinc-100 pb-2 flex items-center gap-1.5">
                        <FiUser className="w-3.5 h-3.5" /> General Details
                      </h3>
                      <div className="space-y-2.5">
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            First Name
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.first_name || "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            Last Name
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.last_name || "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            Gender
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.gender === "M"
                              ? "Male"
                              : student.gender === "F"
                              ? "Female"
                              : student.gender || "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="font-bold text-zinc-400 uppercase tracking-wider text-[11px] border-b border-zinc-100 pb-2 flex items-center gap-1.5">
                        <FiBookOpen className="w-3.5 h-3.5" /> Academic Info
                      </h3>
                      <div className="space-y-2.5">
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            Assigned Class
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.classroom_name || "Unassigned"}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            Class Adviser
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.adviser_name || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-400 block text-[11px]">
                            Date Added
                          </span>
                          <span className="font-medium text-zinc-800">
                            {student.created_at
                              ? new Date(
                                  student.created_at
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* View 2: Dress Code / Infractions */}
            {activeTab === "infractions" && (
              <div className="space-y-3">
                <h3 className="font-bold text-zinc-400 uppercase tracking-wider text-[11px] mb-3 flex items-center gap-1.5">
                  <FiShield className="w-3.5 h-3.5" /> Infraction Records History
                </h3>
                <div className="border border-zinc-200/80 rounded-lg overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                        <th className="py-2.5 px-4">Date</th>
                        <th className="py-2.5 px-4">Type</th>
                        <th className="py-2.5 px-4">Notes</th>
                        <th className="py-2.5 px-4">Recorded By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
                      {infractions.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="py-8 text-center text-zinc-400"
                          >
                            No infraction records found for this student.
                          </td>
                        </tr>
                      ) : (
                        infractions.map((item, idx) => (
                          <tr
                            key={item.id || idx}
                            className="hover:bg-zinc-50/60 transition-colors"
                          >
                            <td className="py-2.5 px-4 font-mono text-[11px] text-zinc-800">
                              {item.date || "—"}
                            </td>
                            <td className="py-2.5 px-4 font-medium text-zinc-800">
                              {item.type || "Uniform"}
                            </td>
                            <td className="py-2.5 px-4 text-zinc-500">
                              {item.notes || "—"}
                            </td>
                            <td className="py-2.5 px-4 text-zinc-500">
                              {item.recorded_by || "System"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* View 3: Pass Card Placeholder */}
            {activeTab === "passCard" && (
              <div className="p-8 text-center text-zinc-400 text-xs">
                Pass card details and QR code view.
              </div>
            )}

            {/* View 4: Departure Logs Placeholder */}
            {activeTab === "departureLogs" && (
              <div className="p-8 text-center text-zinc-400 text-xs">
                Student departure scan logs.
              </div>
            )}

            {/* View 5: Behavior Log Placeholder */}
            {activeTab === "behavior" && (
              <div className="p-8 text-center text-zinc-400 text-xs">
                Student behavior and commendation records.
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Navigation Sidebar */}
        <div
          className={`transition-all duration-300 ${
            isSidebarOpen
              ? "lg:col-span-4 xl:col-span-3 block"
              : "lg:col-span-12 flex justify-end"
          }`}
        >
          <StudentRightSidebar
            isOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen((prev) => !prev)}
            activeTab={activeTab}
            onSelectTab={(tabKey) => setActiveTab(tabKey)}
            student={student}
            counts={{
              infractions: infractions.length,
            }}
          />
        </div>
      </div>
    </div>
  );
}